import { tassign } from 'tassign';
import * as _ from 'lodash';

// config
import { navigationConfig } from '../config/navigation.config';

// Own feature
import { INavGraphDisplayOptionEdges } from '../model/nav-display-edgegroup-pairs';
import { INavRequestOption, NAV_REQUEST_OPTIONS_INITIAL_STATE } from '../model/nav-request-option.interface';
import { INavDisplayOptions, NAV_DISPLAY_OPTIONS_INITIAL_STATE } from '../model/nav-display-options.interface';
import { INavGraph2EdgeDefinitionConfig } from '../model/nav-graph2-edgedef-config.interface';

import {
    // -- Reset State ----------------------------
    NAVIGATION_RESET_STATE_ALL_EXCEPT_CONFIG,

    // -- Config ---------------------------------
    NAVIGATION_STORE_CONFIG,

    // -- Graph 1 --------------------------------
    NAVIGATION_STORE_RESULT_1ST_INITIAL,
    NAVIGATION_STORE_RESULT_1ST_EXTENDED,
    NAVIGATION_STORE_RESULT_1ST_DETAIL,
    NAVIGATION_STORE_1ST_DISPLAY_OPTIONS,
    NAVIGATION_STORE_1ST_REQUEST_OPTIONS,
    NAVIGATION_STORE_1ST_AVAILABLE_EDGEGROUP_PAIRS,
    NAVIGATION_STORE_1ST_DISPLAY_OPTION_EDGES,

    // -- Graph 2 --------------------------------
    NAVIGATION_STORE_RESULT_2ND_INITIAL,
    NAVIGATION_STORE_RESULT_2ND_EXTENDED,
    NAVIGATION_STORE_RESULT_2ND_DETAIL,
    NAVIGATION_STORE_2ND_DISPLAY_OPTIONS,
    NAVIGATION_STORE_2ND_REQUEST_OPTIONS,
    NAVIGATION_STORE_2ND_AVAILABLE_EDGEGROUP_PAIRS,
    NAVIGATION_STORE_2ND_DISPLAY_OPTION_EDGES,
    NAVIGATION_STORE_2ND_EDGE_DEFINITION_CONFIG,

    // -- Schema ---------------------------------
    NAVIGATION_STORE_SCHEMA
} from './actions';

export interface INavigationState {
    // -- Config ---------------------------------
    config: INavConfig | null;

    // -- Graph 1 --------------------------------
    elementsResult1stInitial: any | null;
    elementsResult1stExtended: any | null;
    element1stDetail: any | null;
    element1stDisplayOptions: INavDisplayOptions | null;
    element1stRequestOptions: INavRequestOption | null;
    element1stDisplayOptionEdges: INavGraphDisplayOptionEdges[] | null;

    // -- Graph 2 --------------------------------
    elementsResult2stInitial: any | null;
    elementsResult2stExtended: any | null;
    element2stDetail: any | null;
    element2stDisplayOptions: INavDisplayOptions | null;
    element2stRequestOptions: INavRequestOption | null;
    element2stDisplayOptionEdges: INavGraphDisplayOptionEdges[] | null;
    element2stEdgeDefinitionConfig: INavGraph2EdgeDefinitionConfig | null;

    // -- Schema ---------------------------------
    schema: any | null;
}

export interface INavConfig {
    appearance: IAppearanceConfig;
    dictionary: Array<IDocumentDictionaryEntry | IEdgeDictionaryEntry | ITypeDictionaryEntry>;
    edgeDefinitions: IEdgeDefinitionConfig;
    search: ISearchConfig;
}

export interface IAppearanceConfig {
    graph: IGraphAppearanceConfig;
    list: IListAppearanceConfig;
    subtype: ISubtypeAppearanceConfig;
}

export interface IGraphAppearanceConfig {
    documents: {
        [key: string]: IGraphDocumentAppearanceConfig;
    };
    edges: {
        [key: string]: IGraphEdgeAppearanceConfig;
    };
}

export interface IGraphDocumentAppearanceConfig {
    background?: string;
    border?: string;
    fontcolor?: string;
    shape?: string;
}

export interface IGraphEdgeAppearanceConfig {
    color?: string;
    dashes?: string;
    fontcolor?: string;
    fontsize?: string;
    width?: string;
}

export interface IListAppearanceConfig {
    [key: string]: {
        [key: string]: string;
    };
}

export interface ISubtypeAppearanceConfig {
    [key: string]: string | null | undefined;
}

export interface IDictionaryEntry {
    key: string;
    type: 'nomen' | 'verb' | 'typeKey';
    searchable: boolean;
    collectionType?: 'document' | 'edge';
    short?: string;
    long?: string;
    genus?: 'm' | 'n' | 'f';
    fwd?: string;
    bkwd?: string;
    collection?: string;
    property?: string;
    aktion?: string[];
}

export interface IDocumentDictionaryEntry extends IDictionaryEntry {
    type: 'nomen';
    collectionType: 'document';
    short: string;
    long: string;
    genus: 'm' | 'n' | 'f';
}

export interface IEdgeDictionaryEntry extends IDictionaryEntry {
    type: 'verb';
    collectionType: 'edge';
    fwd: string;
    bkwd: string;
}

export interface ITypeDictionaryEntry extends IDictionaryEntry {
    type: 'typeKey';
    collection: string;
    short: string;
    long: string;
    property: string;
    aktion?: string[];
}

export interface IEdgeDefinitionConfig {
    [key: string]: IEdgeDefinition;
}

export interface IEdgeDefinition {
    [key: string]: {
        to?: string[];
        from?: string[];
    };
}

export interface ISearchConfig {
    edges: string[];
}

export const NAVIGATION_INITIAL_STATE: INavigationState = {
    // -- Config ---------------------------------
    config: null,

    // -- Graph 1 --------------------------------
    elementsResult1stInitial: navigationConfig.elementsResultInitial,
    elementsResult1stExtended: null,
    element1stDetail: null,
    element1stDisplayOptions: NAV_DISPLAY_OPTIONS_INITIAL_STATE,
    element1stRequestOptions: NAV_REQUEST_OPTIONS_INITIAL_STATE,
    element1stDisplayOptionEdges: [],

    // -- Graph 2 --------------------------------
    elementsResult2stInitial: navigationConfig.elementsResultInitial,
    elementsResult2stExtended: null,
    element2stDetail: null,
    element2stDisplayOptions: NAV_DISPLAY_OPTIONS_INITIAL_STATE,
    element2stRequestOptions: NAV_REQUEST_OPTIONS_INITIAL_STATE,
    element2stDisplayOptionEdges: [],
    element2stEdgeDefinitionConfig: null,

    // -- Schema ---------------------------------
    schema: null
};

export function navigationReducer(state: INavigationState = NAVIGATION_INITIAL_STATE, action): INavigationState {
    switch (action.type) {
        // -- 0. State ------------------------------------------------------------------------------
        case NAVIGATION_RESET_STATE_ALL_EXCEPT_CONFIG: return resetStateAllExceptConfig(state, action);

        // -- 1. Initial Config ---------------------------------------------------------------------
        case NAVIGATION_STORE_CONFIG: return storeConfigJss(state, action);

        // -- 2. Element Graph ----------------------------------------------------------------------
        case NAVIGATION_STORE_RESULT_1ST_INITIAL: return storeElementResult1stInitial(state, action);
        case NAVIGATION_STORE_RESULT_1ST_EXTENDED: return storeElementsResult1stExtended(state, action);
        case NAVIGATION_STORE_RESULT_1ST_DETAIL: return storeElementResult1stDetail(state, action);
        case NAVIGATION_STORE_1ST_DISPLAY_OPTIONS: return storeElement1stDisplayOptions(state, action);
        case NAVIGATION_STORE_1ST_REQUEST_OPTIONS: return storeElement1stRequestOptions(state, action);
        case NAVIGATION_STORE_1ST_DISPLAY_OPTION_EDGES: return storeElements1stDisplayOptionEdges(state, action);

        // -- 3. Search Graph -----------------------------------------------------------------------
        case NAVIGATION_STORE_RESULT_2ND_INITIAL: return storeElementResult2stInitial(state, action);
        case NAVIGATION_STORE_RESULT_2ND_EXTENDED: return storeElementsResult2stExtended(state, action);
        case NAVIGATION_STORE_RESULT_2ND_DETAIL: return storeElementResult2stDetail(state, action);
        case NAVIGATION_STORE_2ND_DISPLAY_OPTIONS: return storeElement2stDisplayOptions(state, action);
        case NAVIGATION_STORE_2ND_REQUEST_OPTIONS: return storeElement2stRequestOptions(state, action);
        case NAVIGATION_STORE_2ND_DISPLAY_OPTION_EDGES: return storeElements2stDisplayOptionEdges(state, action);
        case NAVIGATION_STORE_2ND_EDGE_DEFINITION_CONFIG: return storeElements2stEdgeDefinitionConfig(state, action);

        // -- 4. Schema -----------------------------------------------------------------------------
        case NAVIGATION_STORE_SCHEMA: return storeSchema(state, action);

        default:
            console.error('Unknown action type in Navigator-Reducer', action.type);
    }
    return state;
}

// ------------------------------------------------------------------------------------------------
// -- Reset State
// ------------------------------------------------------------------------------------------------
function resetStateAllExceptConfig(state, action) {
    // temp store config
    const c: any = state.config;
    // take initial state
    const s: INavigationState = NAVIGATION_INITIAL_STATE;
    // restore config
    s.config = c;
    return Object.assign({}, state, s);
}

// ------------------------------------------------------------------------------------------------
// -- Config
// ------------------------------------------------------------------------------------------------
function storeConfigJss(state, { type: type, payload: { object: jsObj } }) {
    return tassign(state, {
        config: jsObj
    });
}

// ------------------------------------------------------------------------------------------------
// -- Graph 1
// ------------------------------------------------------------------------------------------------
function storeElementResult1stInitial(state, { type: type, payload: { object: jsObj } }) {
    return tassign(state, {
        elementsResult1stInitial: jsObj,
        elementsResult1stExtended: null
    });
}
function storeElementsResult1stExtended(state, { type: type, payload: { object: jsObj } }) {
    return tassign(state, {
        elementsResult1stExtended: jsObj
    });
}
function storeElementResult1stDetail(state, { type: type, payload: { object: jsObj } }) {
    return tassign(state, {
        element1stDetail: jsObj
    });
}

function storeElement1stDisplayOptions(state, { type: type, payload: { object: options } }) {
    return tassign(state, {
        element1stDisplayOptions: options
    });
}
function storeElement1stRequestOptions(state, { type: type, payload: { object: options } }) {
    return tassign(state, {
        element1stRequestOptions: options
    });
}

function storeElements1stDisplayOptionEdges(state, { type: type, payload: { object: obj } }) {
    return tassign(state, {
        element1stDisplayOptionEdges: _.cloneDeep(obj)
    });
}

// ------------------------------------------------------------------------------------------------
// -- Graph 1
// ------------------------------------------------------------------------------------------------
function storeElementResult2stInitial(state, { type: type, payload: { object: jsObj } }) {
    return tassign(state, {
        elementsResult2stInitial: jsObj,
        elementsResult2stExtended: null
    });
}
function storeElementsResult2stExtended(state, { type: type, payload: { object: jsObj } }) {
    return tassign(state, {
        elementsResult2stExtended: jsObj
    });
}
function storeElementResult2stDetail(state, { type: type, payload: { object: jsObj } }) {
    return tassign(state, {
        element2stDetail: jsObj
    });
}

function storeElement2stDisplayOptions(state, { type: type, payload: { object: options } }) {
    return tassign(state, {
        element2stDisplayOptions: options
    });
}
function storeElement2stRequestOptions(state, { type: type, payload: { object: options } }) {
    return tassign(state, {
        element2stRequestOptions: options
    });
}

function storeElements2stDisplayOptionEdges(state, { type: type, payload: { object: obj } }) {
    return tassign(state, {
        element2stDisplayOptionEdges: _.cloneDeep(obj)
    });
}

function storeElements2stEdgeDefinitionConfig(state, { type: type, payload: { object: obj } }) {
    return tassign(state, {
        element2stEdgeDefinitionConfig: obj
    });
}

// 4. Schema --------------------------------------------------------------------------------------
function storeSchema(state, { type: type, payload: { object: jsObj } }) {
    return tassign(state, {
        schema: jsObj
    });
}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/nav/store/store.ts